import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { fetchTestimonials } from '../utils/api'; // Assuming api.js is in utils directory
import { API_BASE_URL } from '../utils/api'; // Import API_BASE_URL for image URLs
import { Link } from 'react-router-dom';
import q1 from '../../assets/testinomials/quote 1.svg';
import q2 from '../../assets/testinomials/quote 2.svg';
import fallbackImage from '../../assets/fallback.png'; // Fallback image
import { FiArrowUpRight } from 'react-icons/fi';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setIsLoading(true);
        const data = await fetchTestimonials();

        // Filter out testimonials without images
        const testimonialsWithImages = data.filter((item) => item.image);

        // Take the 10 newest with images
        const latestTestimonials = testimonialsWithImages.slice(-10).reverse();

        const mappedTestimonials = latestTestimonials.map((item) => ({
          _id: item._id,
          img: `${API_BASE_URL}/${item.image}`, // image is guaranteed to exist
          text: item.description,
          name: item.name,
          starRating: item.starRating,
        }));

        setTestimonials(mappedTestimonials);
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  // Skeleton loader component for testimonials
  const SkeletonLoader = () => (
    <div className='animate-pulse'>
      <div className='mb-4 flex justify-start sm:mb-6 md:ml-48'>
        <img
          src={q1}
          alt='Quote Start'
          className='h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16'
        />
      </div>
      <div className='mx-auto min-h-[110px] max-w-xl px-2 sm:max-w-2xl'>
        <div className='mx-auto mb-2 h-4 w-3/4 rounded bg-gray-300'></div>
        <div className='mx-auto mb-2 h-4 w-5/6 rounded bg-gray-300'></div>
        <div className='mx-auto h-4 w-2/3 rounded bg-gray-300'></div>
      </div>
      <div className='flex justify-end md:mr-48'>
        <img
          src={q2}
          alt='Quote End'
          className='h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16'
        />
      </div>
      <div className='flex items-center justify-center'>
        <Swiper
          modules={[Autoplay]}
          spaceBetween={10}
          slidesPerView={5}
          className='w-full max-w-[850px] px-2 py-6 sm:py-10'
        >
          {[...Array(5)].map((_, idx) => (
            <SwiperSlide
              key={idx}
              className='flex items-center justify-center'
            >
              <div className='rounded-full border-2 border-gray-300'>
                <div className='h-16 w-16 rounded-full bg-gray-300 sm:h-20 sm:w-20 md:h-28 md:w-28'></div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );

  return (
    <>
      <div className='flex flex-col justify-center bg-[#EDEFFF] px-4 py-10'>
        <p className='mb-10 text-center text-2xl font-medium leading-tight tracking-normal text-[#495BFF] md:text-[40px]'>
          Voices of Our Travelers
        </p>
        {/* <div className="flex max-w-full items-center mb-10 justify-between ">
          <p className="text-2xl font-medium leading-tight tracking-normal text-[#495BFF] md:text-[40px]">
          Voices of Our Travelers
          </p>
          <Link
            to="/testimonials"
            className="group flex items-center gap-1 text-[20px] text-[#495BFF]"
          >
            See all
            <FiArrowUpRight className="transform text-[24px] text-[#55E6A5] transition-transform duration-500 group-hover:rotate-45" />
          </Link>
        </div> */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className='mb-4 flex justify-start sm:mb-6 md:ml-48'>
              <img
                src={q1}
                alt='Quote Start'
                className='h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16'
              />
            </div>
            <p className='mx-auto min-h-[140px] max-w-xl px-2 text-center text-lg font-semibold text-[#3F5F7D] sm:mb-6 sm:max-w-2xl lg:min-h-[100px]'>
              {testimonials[activeIndex]?.text || 'No testimonials available.'}
            </p>
            <div className='flex justify-end md:mr-48'>
              <img
                src={q2}
                alt='Quote End'
                className='h-8 w-8 sm:h-10 sm:w-10 md:h-16 md:w-16'
              />
            </div>
            <div className='flex items-center justify-center'>
              <Swiper
                modules={[Autoplay]}
                spaceBetween={10}
                loop={testimonials.length > 1} // Only loop if there are multiple testimonials
                centeredSlides={true}
                autoplay={{ delay: 2500, disableOnInteraction: false }}
                breakpoints={{
                  0: { slidesPerView: 3 }, // mobile
                  640: { slidesPerView: 4 }, // sm
                  1024: { slidesPerView: 5 }, // desktop
                }}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                className='w-full max-w-[850px] px-2 py-6 sm:py-10'
              >
                {testimonials.map((t, idx) => (
                  <SwiperSlide
                    key={t._id || idx}
                    className='flex items-center justify-center'
                  >
                    <div
                      className={`rounded-full border-2 transition-transform duration-500 ${
                        activeIndex === idx
                          ? 'scale-110 border-white'
                          : 'scale-90 border-gray-300 opacity-70'
                      }`}
                    >
                      <img
                        src={t.img}
                        alt={`Traveler ${t.name}`}
                        className='h-16 w-16 rounded-full object-cover shadow-md sm:h-20 sm:w-20 md:h-28 md:w-28'
                        onError={(e) => (e.target.src = fallbackImage)}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <div className='mt-10 flex justify-center'>
              <Link
                to='/testimonials'
                className='relative inline-flex h-[34px] w-[140px] items-center justify-center rounded-[6px] bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-[1px] transition-all duration-500 hover:from-[#55E6A5] hover:to-[#495BFF] sm:h-[40px] sm:w-[170px] md:h-[46px] md:w-[200px]'
              >
                <span className='flex h-full w-full items-center justify-center bg-transparent text-[13px] font-bold leading-tight text-white sm:text-[15px] md:text-[16px]'>
                  View all
                </span>
              </Link>
            </div>
          </>
        )}
      </div>

      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 1200 100'
        preserveAspectRatio='none'
        style={{
          backgroundColor: 'transparent',
          display: 'block',
          height: '30px',
          position: 'relative',
          top: '-1px',
          width: '100%',
        }}
      >
        <path
          d='M851.8,100c125,0,288.3-45,348.2-64V0H0v44c3.7-1,7.3-1.9,11-2.9C80.7,22,151.7,10.8,223.5,6.3C276.7,2.9,330,4,383,9.8 c52.2,5.7,103.3,16.2,153.4,32.8C623.9,71.3,726.8,100,851.8,100z'
          style={{ fill: '#EDEFFF' }}
        />
      </svg>
    </>
  );
};

export default Testimonials;
